import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled, prefilledRange } from '../lib/reportPrefill';
import { transactionalDefaultRange } from '../reportDateDefaults';
import { maxReportDate } from '../lib/reportDates';
import ReportingCurrencyField from './ReportingCurrencyField';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Significant Contributions/Withdrawals Popup
 * 
 * Popup for generating Significant Contributions/Withdrawals reports.
 * Requires date range input and threshold percentage.
 */
const SignificantContributionsWithdrawalsPopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [thresholdPercent, setThresholdPercent] = useState(10.0);
  const [currency] = useState('USD');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Transactional report: default to the last full calendar year (PM Q18).
  const defaultRange = transactionalDefaultRange();

  useEffect(() => {
    if (isOpen) {
      setSelection({ selectedItem: null, params: {} });
      const range = prefilledRange(initialParams, { start: defaultRange.start, end: defaultRange.end });
      setStartDate(range.start);
      setEndDate(range.end);
      setThresholdPercent(prefilled(initialParams, 'threshold_pct', 10.0));
    }
  }, [isOpen, defaultRange.start, defaultRange.end, initialParams]);

  // The single thin-params builder used by handleConfirm.
  const buildParams = (sel) => ({
    ...sel.params,
    date_range: {
      start: startDate,
      end: endDate
    },
    date: startDate, // For backward compatibility
    threshold_pct: thresholdPercent,
    threshold_percent: thresholdPercent, // legacy key
    currency: currency,
  });

  const validateForm = (sel = selection) => {
    const newErrors = {};

    if (!sel.selectedItem) {
      newErrors.portfolio = 'Portfolio is required';
    }

    if (!startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      newErrors.dateRange = 'Start date must be before end date';
    }

    if (thresholdPercent < 0 || thresholdPercent > 100) {
      newErrors.thresholdPercent = 'Threshold must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm(sel)) {
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(buildParams(sel));
      onClose();
    } catch (error) {
      console.error('Report generation error:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
        darkMode ? 'bg-[#141414]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {reportInfo?.name || 'Significant Contributions/Withdrawals'}
          </h3>
          <button
            onClick={onClose}
            className={`transition-colors ${
              darkMode 
                ? 'text-gray-400 hover:text-gray-300' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Description */}
        {reportInfo?.description && (
          <div className={`mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {reportInfo.description}
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* Portfolio Selector (groups vs accounts, @@ aggregate, type-routed) */}
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleConfirm}  disabled={isLoading}/>
            {errors.portfolio && (
              <p className="text-red-500 text-xs mt-1">{errors.portfolio}</p>
            )}
          </div>

          {/* Start Date Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Start Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={startDate}
              max={maxReportDate()}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (errors.startDate) {
                  setErrors(prev => ({ ...prev, startDate: null }));
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.startDate ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.startDate && (
              <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>
            )}
          </div>

          {/* End Date Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              End Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={endDate}
              max={maxReportDate()}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (errors.endDate) {
                  setErrors(prev => ({ ...prev, endDate: null }));
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.endDate ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.endDate && (
              <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>
            )}
            {errors.dateRange && (
              <p className="text-red-500 text-xs mt-1">{errors.dateRange}</p>
            )}
          </div>

          {/* Threshold Percentage Field */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Threshold as a percent <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={thresholdPercent}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setThresholdPercent(isNaN(value) ? 0 : value);
                if (errors.thresholdPercent) {
                  setErrors(prev => ({ ...prev, thresholdPercent: null }));
                }
              }}
              min="0"
              max="100"
              step="0.1"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                darkMode
                  ? 'bg-[#1a1a1a] border-white/10 text-white'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.thresholdPercent ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.thresholdPercent && (
              <p className="text-red-500 text-xs mt-1">{errors.thresholdPercent}</p>
            )}
          </div>

          {/* Currency Field */}
          <ReportingCurrencyField darkMode={darkMode} />

          {errors.submit && (
            <div className={`p-3 rounded-lg border ${
              darkMode 
                ? 'bg-red-900/20 border-red-800' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel || onClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300 hover:bg-[#1a1a1a]' : ''}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Generating...
              </>
            ) : (
              'OK'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignificantContributionsWithdrawalsPopup;

